import { FunctionComponent, useContext, useEffect, useState } from 'react'
import {  FormControl,  FormHelperText, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IEditLocation, ILocation, IPostDeactivateLocation } from '../../../shared/services/location/location.model';
import LocationService from '../../../shared/services/location/location.service';
import { ScreenLabelsContext } from '../../../context/screen-labels/screen-labels';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';

export const DeactivateLocation: FunctionComponent<{
  open: boolean,
  location: IEditLocation,
  closeHandler: () => void,
  submitHandler: (values: IPostDeactivateLocation) => void,
  isSubmitting: boolean,
  errorMessage?: string,
}> = ({
  open,
  location,
  closeHandler,
  submitHandler,
  isSubmitting,
  errorMessage,
}) => {

    const locationService = LocationService.getInstance();
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const [allOtherlocations, setAllOtherLocations] = useState<ILocation[]>()

    useEffect(() => {
      locationService.getActiveLocations(true)
        .then((response: ILocation[]) => {
          const filteredLocations = response.filter(r => r.id !== location.id)
          setAllOtherLocations(filteredLocations)
        })
        .catch(err => console.log(err))
    }, [])

    const deactivateLocationSchema = Yup.object().shape({
      locationId: Yup.string().required(),
      newPrimaryLocationId: Yup.string().when([], {
        is: () => location.isPrimaryLocation,
        then: schema => schema.required(`You need to select a new ${screenLabelsContext.getLabel('location', { plural: false })}`),
        otherwise: schema => schema.notRequired()
      }),
    })

    const formik = useFormik({
      initialValues: {
        locationId: location.id,
        newPrimaryLocationId: '',
      },
      validationSchema: deactivateLocationSchema,
      onSubmit: (values) => {
        submitHandler(values)
      }
    });

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }


    return (
      <DialogWrapper open={open} onClose={closeHandler} title="Confirmation" cancelHandler={cancelHandler} submitHandler={formik.submitForm} isSubmitting={isSubmitting}>
        <Typography variant='subtitle1'>By deactivating this {screenLabelsContext.getLabel('location', { plural: false })}, you will not be able to add any more emissions to the {screenLabelsContext.getLabel('location', { plural: false })}. This action cannot be undone.</Typography>
        {location.isPrimaryLocation &&
          <Stack sx={{ mt: 2 }}>
            <Typography variant='body1' sx={{ mb: 2 }}>This {screenLabelsContext.getLabel('location', { plural: false })} was recorded as your primary {screenLabelsContext.getLabel('location', { plural: false })}, you must therefore select a new primary {screenLabelsContext.getLabel('location', { plural: false })} </Typography>
            <FormControl fullWidth variant='outlined'>
              <InputLabel>{screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}</InputLabel>
              <Select
                id='newPrimaryLocationId' name='newPrimaryLocationId'
                value={formik.values.newPrimaryLocationId || ''}
                onChange={formik.handleChange}
                label={screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' })}
                error={formik.touched.newPrimaryLocationId && Boolean(formik.errors.newPrimaryLocationId)}
                fullWidth>
                {allOtherlocations?.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)).map((location, index) => (
                  <MenuItem key={index}
                    value={location.id}>{location.friendlyName}</MenuItem>
                ))}
              </Select>
              {formik.touched.newPrimaryLocationId && formik.errors.newPrimaryLocationId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.newPrimaryLocationId ? <div>{formik.errors.newPrimaryLocationId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          </Stack>}
        {errorMessage &&
          <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
          </FormHelperText>
        }
      </DialogWrapper>
    )
  }